import React, { memo, useEffect, useRef, useState, useMemo } from 'react';
import { DateTime } from 'luxon';
import { connectProSettings } from 'libs/connectProSettings/connectProSettings';
import { getConnectProToken } from 'libs/connectProTokenUtil';
import { useCurrentUser } from 'libs/hooks';
import { enrichSupportingEntities } from 'libs/enrichSupportingEntities';
import { getShipperIdByBookingId } from 'libs/getShipperByBookingId';
import {
  searchBookingsByPrefix,
  searchContainersByPrefix,
} from 'libs/searchEntityIdsByPrefix';
import { getIorIdByWorkOrderId } from 'libs/getIorIdByWorkOrderId';
import { getIorIdByBookingId } from 'libs/getIorIdByBookingId';
import { getRecordType } from '@xbcb/core';
import { getCategoryListForWorkOrder } from './categoryConfig';
import { getWorkOrderHyperlink } from 'libs/getWorkOrderHyperlink';
import {
  mockCaseListProps,
  mockCaseDetailsProps,
  mockCreateCaseProps,
  mockTaskListDetails,
} from './mockProps';

interface ConnectServiceTabWidgetInstance {
  showCreateCase: (props: Record<string, unknown>) => void;
}

interface ConnectServiceTabWidgetAssets {
  ConnectServiceTabWidget: new (
    container: HTMLElement,
    config: Record<string, unknown>,
  ) => ConnectServiceTabWidgetInstance;
}

// Global reference to the widget instance for external access
let globalWidgetInstance: ConnectServiceTabWidgetInstance | null = null;
let globalCreateCaseProps: Record<string, unknown> | null = null;
let globalBaseProps: Record<string, unknown> | null = null;

export const openCreateSellerCaseModal = (): void => {
  if (globalWidgetInstance && globalCreateCaseProps && globalBaseProps) {
    // Expose globals to window for modal access
    (window as any).globalWidgetInstance = globalWidgetInstance;
    (window as any).globalCreateCaseProps = globalCreateCaseProps;
    (window as any).globalBaseProps = globalBaseProps;

    globalWidgetInstance.showCreateCase({
      ...globalBaseProps,
      ...globalCreateCaseProps,
      visible: true,
    });
  }
};

export interface SupportCasesTabProps {
  record: Record<string, unknown>;
}

const SupportCasesTab = ({ record }: SupportCasesTabProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const user = useCurrentUser();
  const userLogin = user.email?.split('@')[0] || '';
  const [accessToken, setAccessToken] = useState('');

  useEffect(() => {
    const fetchToken = async () => {
      const token = await getConnectProToken(userLogin);
      setAccessToken(token);
    };
    void fetchToken();
  }, [userLogin]);

  const baseProps = useMemo(() => {
    const recordId = record?.id;
    const recordType =
      typeof recordId === 'string' ? getRecordType(recordId) : null;
    const categoryList = recordType
      ? getCategoryListForWorkOrder(recordType)
      : [];

    return {
      categoryList,
      stage: connectProSettings.domain,
      panelView: 'OC' as const,
      defaultAccessToken: accessToken,
      connectServiceBaseAccessTokenProvider: () =>
        getConnectProToken(userLogin),
      weblabProvider: new Map([]),
      widgetWidth: '100%',
      widgetHeight: '100%',
      onGetTaskListDetailsFunction: mockTaskListDetails,
      timezone: DateTime.local().zoneName,
      EnrichSupportingEntities: enrichSupportingEntities,
      prefillAdditionalEntityTypes: new Map([['WORK_ORDER', recordId]]),
      getWorkOrderHyperlink,
      getShipperIdByBookingId,
      searchBookingsByPrefix,
      searchContainersByPrefix,
      getIorIdByWorkOrderId,
      getIorIdByBookingId,
      workOrderStatus: record?.status,
    };
  }, [accessToken, userLogin, record?.id, record?.status]);

  const caseListProps = useMemo(
    () => ({
      ...mockCaseListProps,
      entityMap: new Map([['WORK_ORDER', record?.id]]),
      caseSearchParams: {
        ...mockCaseListProps.caseSearchParams,
        caseFilter: {
          field: {
            quantifier: 'EQUAL',
            field: 'ENTITY_ID',
            value: record?.id,
          },
        },
      },
    }),
    [record?.id],
  );

  const caseDetailsProps = mockCaseDetailsProps;

  const createCaseProps = useMemo(
    () => ({
      ...mockCreateCaseProps,
      entityMap: new Map([['WORK_ORDER', record?.id]]),
    }),
    [record?.id],
  );

  const tabWidgetProps = useMemo(
    () => ({
      position: 'overlay-right',
      id: 'sidepanelcontainer',
      baseProps,
      caseListProps,
      caseDetailsProps,
      createCaseProps,
    }),
    [baseProps, caseListProps, caseDetailsProps, createCaseProps],
  );

  useEffect(() => {
    if (!accessToken) return; // Wait for token to be available

    const scriptSrc = connectProSettings.endpoints.tabWidgetEndpoint;
    let interval: NodeJS.Timeout;
    let timeout: NodeJS.Timeout;

    const initializeWidget = async () => {
      if (!containerRef.current) return;

      // Check if script already exists
      const existingScript = document.querySelector(
        `script[src="${scriptSrc}"]`,
      );

      if (!existingScript) {
        // Load the widget script
        const script = document.createElement('script');
        script.src = scriptSrc;
        script.async = true;
        document.body.appendChild(script);
      }

      // Function to render the widget
      const renderWidget = () => {
        if (window.XbcbConnectProWidgetAssets && containerRef.current) {
          try {
            const widgetInstance = new (
              window.XbcbConnectProWidgetAssets as unknown as ConnectServiceTabWidgetAssets
            ).ConnectServiceTabWidget(containerRef.current, tabWidgetProps);
            globalWidgetInstance = widgetInstance;
            globalCreateCaseProps = createCaseProps;
            globalBaseProps = baseProps;
          } catch (error) {
            console.error('Failed to initialize ConnectPro widget:', error);
          }
        }
      };

      // Check if widget is already loaded
      if (window.XbcbConnectProWidgetAssets) {
        renderWidget();
        return;
      }

      // Poll for widget to load
      interval = setInterval(() => {
        if (window.XbcbConnectProWidgetAssets) {
          renderWidget();
          clearInterval(interval);
          clearTimeout(timeout);
        }
      }, 500);

      // Stop polling after 30 seconds
      timeout = setTimeout(() => {
        clearInterval(interval);
        console.warn('Connect Pro tab widget failed to load within 30 seconds');
      }, 30000);
    };

    void initializeWidget();

    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
      // Remove script on unmount
      const script = document.querySelector(`script[src="${scriptSrc}"]`);
      if (script) {
        document.body.removeChild(script);
      }
    };
  }, [accessToken, tabWidgetProps, createCaseProps, baseProps]);

  return (
    <div
      ref={containerRef}
      style={{ height: '100%', minHeight: '600px', width: '100%' }}
    />
  );
};

export default memo(SupportCasesTab);
